import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatStepper } from '@angular/material/stepper';

import { UrlService } from '@service/url/url.service';
import {
	IS_EMPTY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { CustomRestService } from '@service/customRest/custom-rest.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { BUCKET_NAMES, CARGOCHIEF_KEYS, S3_CSV_TEMPLATE } from '@domain/constants/app-variables';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { FirstStepComponent } from './first-step/first-step.component';
import { SharedService } from '@service/shared/shared.service';
import { messageType, messageTypeEnum } from '@domain/constants/carrier-upload-message-type';

@Component({
	selector: 'app-upload-carriers',
	templateUrl: './upload-carriers.component.html',
	styleUrls: ['./upload-carriers.component.scss'],
})
export class UploadCarriersComponent implements OnInit {
	@Input() accountId = null;
	@Input() fullWidth: boolean = false;
	@Input() text: string = '';
	@Input() useButton: boolean = false;
	@Input() useText: boolean = false;
	@Input() useMenuButton: boolean = false;
	@Input() title: string = 'Upload Carriers';
	@Input() fireSegmentEvents: boolean = false;
	@Input() textClass: string = 'primary-text';
	@Input() messageType: messageType;
	@Input() isExcluded: boolean = false;

	@Output() carriersCSVUploaded = new EventEmitter<boolean>();
	@Output() updateCarrierWarmupSetting = new EventEmitter<boolean>();

	constructor(
		public dialog: MatDialog,
		private cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService,
		private utilService: UtilService
	) {}

	ngOnInit() {}

	async uploadCarriersDialog() {
		if (this.fireSegmentEvents) {
			const eventProperties = Object.assign({
				user: this.utilService.getUserDataForSegmentEvent(),
			});
			this.segmentWrapper.fireSegmentEvent(
				SegmentEventsConstants.user_clicked_upload_carriers,
				eventProperties
			);
		}
		if (this.useMenuButton) {
			const accountCarrierMapCount = this.cargochief.getItem(
				CARGOCHIEF_KEYS.ACCOUNT_CARRIER_MAP_COUNT
			);
			if (
				IS_NOT_NULL_OR_UNDEFINED(accountCarrierMapCount) &&
				Number(JSON.parse(accountCarrierMapCount)) === 0
			) {
				this.messageType = messageTypeEnum.FOR_AUTOMATING_BA;
			}
		}
		const dialogRef = this.dialog.open(UploadCarriersDialog, {
			width: '1000px',
			data: {
				accountId: this.accountId,
				title: this.title,
				messageType: this.messageType,
				isExcluded: this.isExcluded
			},
		});
		const result = await dialogRef.afterClosed().toPromise();
		if (IS_NOT_NULL_OR_UNDEFINED(result)) {
			this.cargochief.setItem({ [CARGOCHIEF_KEYS.CARRIERS_CSV_UPLOADED]: true });
			// Always signal the host page that a carrier CSV upload finished so it can react
			// (e.g. the carriers automation page scrolls back to the top to surface the new import row).
			this.carriersCSVUploaded.emit(true);
			if (this.fireSegmentEvents) {
				const account = this.cargochief.getAccount();
				if (IS_NULL_OR_UNDEFINED(account['emailMappedCarriers'])) {
					const { isCarrierWarmupConfirmed } = await this.showConfirmationDialog();
					this.updateCarrierWarmupSetting.emit(isCarrierWarmupConfirmed);
				}
			}
		}
	}

	async showConfirmationDialog() {
		const dialogRef = this.dialog.open(BAUploadCarriersDialog, {
			width: '600px',
			data: {},
			disableClose: true,
		});
		return dialogRef.afterClosed().toPromise();
	}
}

@Component({
	selector: 'app-upload-carriers-dialog',
	templateUrl: 'modal.template.html',
})
export class UploadCarriersDialog {
	isUpdating = false;
	fileToUpload: File = null;
	fileData = null;
	accountId = null;
	title: string;
	isExcluded = false;
	messageType: messageTypeEnum;
	S3_CSV_TEMPLATE = S3_CSV_TEMPLATE;
	MAX_ERROR_MSGS = 10;

	expectedHeaders: Array<string> = [
		'business_name',
		'mc',
		'dot',
		'contact_name',
		'phone',
		'email',
		'hazmat',
	];
	fileHeaders: Array<string> = [];
	fileHeaders2: Array<string> = [];
	requiredData: Array<string> = ['business_name'];
	errorMsgs: Array<string> = [];
	messageTypeEnum = messageTypeEnum;
	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;

	@ViewChild('stepper') stepper: MatStepper;
	@ViewChild(FirstStepComponent) uploadFileComponent: FirstStepComponent;

	constructor(
		public dialogRef: MatDialogRef<UploadCarriersDialog>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private urlService: UrlService,
		private restService: RestService,
		private utilService: UtilService,
		private customRestService: CustomRestService,
		private cargochief: Cargochief,
		private sharedService: SharedService
	) {
		this.accountId = this.data.accountId;
		this.title = this.data.title;
		this.messageType = this.data.messageType;
		this.isExcluded = this.data.isExcluded;
	}

	closeDialog(data: any = null): void {
		this.dialogRef.close(data);
	}

	afterFileUpload({ fileToUpload, fileData }) {
		this.fileToUpload = fileToUpload;
		this.fileData = fileData;
		if (IS_NOT_NULL_OR_UNDEFINED(fileData)) {
			if (fileData.length === 0) {
				this.utilService.showSnackbar('Empty file.', SNACKBAR.ERROR, SNACKBAR.ACTION);
				this.clearFile();
				return;
			} else if (fileData.length > 0) {
				this.fileHeaders = fileData[0];
				this.fileHeaders2 = fileData[1];
			}
		}
		this.isValidFileData();
		if (
			this.fileHeaders2[0].toLowerCase() == 'required' ||
			this.fileHeaders2[0].toLowerCase() == 'optional'
		) {
			this.fileData.splice(1, 1); // remove the required / optional row under the headers from the file. if using the v2 template.
		}
		this.stepper.next();
	}

	isValidFileData() {
		this.errorMsgs = [];
		const MC_DOT_ERROR = 'MC or DOT is required. It is missing for';
		this.fileHeaders.map((header) => {
			if (IS_EMPTY(header)) {
				this.errorMsgs.push(`Header row has empty columns. All columns must have a header.`);
			}
		});
		if (IS_NOT_NULL_OR_UNDEFINED(this.fileData) && this.fileData.length > 0) {
			for (let fileCounter = 1; fileCounter < this.fileData.length; fileCounter++) {
				for (
					let requiredHeaderCounter = 0;
					requiredHeaderCounter < this.requiredData.length;
					requiredHeaderCounter++
				) {
					const requiredHeaderIndex: number = this.fileHeaders.indexOf(
						this.requiredData[requiredHeaderCounter]
					);
					if (requiredHeaderIndex >= 0) {
						if (
							IS_EMPTY(this.fileData[fileCounter][requiredHeaderIndex]) &&
							this.errorMsgs.indexOf(
								`${this.requiredData[requiredHeaderCounter]} is required. It is empty in some rows`
							) === -1
						) {
							this.errorMsgs.push(
								`${this.requiredData[requiredHeaderCounter]} is required. It is empty in some rows`
							);
							continue;
						}
						const mcIndex = this.fileHeaders.indexOf('mc'),
							dotIndex = this.fileHeaders.indexOf('dot');
						if (
							IS_EMPTY(this.fileData[fileCounter][mcIndex]) &&
							IS_EMPTY(this.fileData[fileCounter][dotIndex])
						) {
							this.errorMsgs.push(
								`${MC_DOT_ERROR} ${this.fileData[fileCounter][requiredHeaderIndex]}`
							);
						}
					} else {
						if (
							this.errorMsgs.indexOf(
								`${this.requiredData[requiredHeaderCounter]} column is missing from file.`
							) === -1
						) {
							this.errorMsgs.push(
								`${this.requiredData[requiredHeaderCounter]} column is missing from file.`
							);
						}
					}
				}
			}
			this.errorMsgs = this.errorMsgs.slice(0, this.MAX_ERROR_MSGS);
		}
	}

	getSignedUrl() {
		this.isUpdating = true;
		const url = this.urlService.signedUrl(BUCKET_NAMES.BULK_CARRIER, this.fileToUpload?.name);
		this.restService.GET(url).subscribe(
			(result) => {
				this.uploadCSVFileToS3PreSignedUrl(result['data'], result['objectKey']);
			},
			(_) => {
				this.isUpdating = false;
				this.utilService.showSnackbar('Unable to get signed URL.', SNACKBAR.ERROR, SNACKBAR.ACTION);
			}
		);
	}

	uploadCSVFileToS3PreSignedUrl(signedUrl: string, objectId: string) {
		this.customRestService.uploadCsvFileToS3(signedUrl, this.fileToUpload).subscribe(
			(_) => {
				this.isUpdating = false;
				this.addCarriersFromS3CsvFile(objectId);
				this.closeDialog(objectId);
				this.utilService.showSnackbar(
					'Your file was uploaded successfully and is being processed.',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
			},
			(error) => {
				this.isUpdating = false;
				this.utilService.showSnackbar(
					error.data || 'Unable to upload now.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	addCarriersFromS3CsvFile(s3ObjectId: any) {
		let accId = this.cargochief.ACCOUNT_ID;
		if (this.accountId !== null) {
			accId = this.accountId;
		}
		const url = this.urlService.addCarriersFromS3CsvFile(s3ObjectId, accId, this.cargochief.USER_ID,this.isExcluded);
		this.restService.GET(url).subscribe(
			(result) => {},
			(error) => {
				let errorMessage = 'Unable to upload csv file at this time. Please try again later.';
				if (error['data']) {
					errorMessage = error['data'];
				}
				this.utilService.showSnackbar(errorMessage, SNACKBAR.ERROR, SNACKBAR.ACTION);
			}
		);
	}

	clearFile() {
		this.fileToUpload = null;
		this.fileData = null;
	}

	clearValidations() {
		this.fileHeaders = [];
		this.errorMsgs = [];
	}

	resetFileData() {
		this.uploadFileComponent.clearFile();
		this.clearFile();
		this.clearValidations();
	}
}

@Component({
	selector: 'ba-upload-carriers-dialog',
	templateUrl: 'ba-upload-carriers-dialog.html',
})
export class BAUploadCarriersDialog {
	SegmentEventsConstants = SegmentEventsConstants;

	constructor(
		public dialogRef: MatDialogRef<BAUploadCarriersDialog>,
		private segmentWrapper: SegmentWrapperService,
		private utilService: UtilService
	) {}

	closeDialog({
		eventName,
		isCarrierWarmupConfirmed,
	}: {
		eventName: string;
		isCarrierWarmupConfirmed: boolean;
	}): void {
		if (IS_NOT_EMPTY(eventName)) {
			const eventProperties = Object.assign({
				user: this.utilService.getUserDataForSegmentEvent(),
			});
			this.segmentWrapper.fireSegmentEvent(eventName, eventProperties);
		}
		this.dialogRef.close({ isCarrierWarmupConfirmed });
	}
}
